import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Table,
  Typography,
  Space,
  App,
  Select,
  Grid,
  Flex,
  message,
  Radio,
  Tag,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import InputNumberField from "../../components/Input/InputNumber";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  fetchPaymentCode,
  fetchDueVouchers,
  fetchSupplierBalance,
  savePayment,
  fetchPayments,
  fetchAccountBalance,
  fetchCurrentliablityAccounts,
  fetchDirectIncomeAccounts,
} from "./supplierPaymentThunk";
import {
  fetchLoadIntoAccs,
  fetchLoadIntoAccsExcludeCheque,
  fetchTagGroups,
  fetchTagGroupFields,
  fetchSuppliers,
  fetchCustomers,
} from "../../store/commonThunks/lookups";
import {
  fetchDueVouchers as fetchCustomerDueVouchers,
  fetchCustomerBalance,
} from "../CustomerReceipts/customerReceiptThunk";
import SearchListAutoComplete from "../../components/SearchListAutoComplete";
import { useNavigate, useParams } from "react-router-dom";
import {
  dateTimeFormat,
  formatToUtc,
  formatToUtcString,
  getAccessType,
  getDateTimeFromSelectedDate,
} from "../../lib/functions";
import {
  setEntryDate,
  setIsEditPayment,
  setSelectedPayment,
  setRefreshList,
  resetPayments,
  setPaymentCode,
} from "./supplierPaymentSlice";
import { selectAuthInfo } from "../Auth/authSelectors";
import TagCreateForm from "../Settings/Tags/components/TagCreateForm";
import AddNewModal from "../../components/DropDown/AddNewModal";
import useMigrationDate from "../../hooks/useMigrationDate";
import { fetchDueCustomerReturns } from "./supplierPaymentThunk";
const { Title } = Typography;
const { useBreakpoint } = Grid;
const { TextArea } = Input;

// ─── thunk for due customer returns ──────────────────────────────────────────
// Add this to supplierPaymentThunk.js:
//
// export const fetchDueCustomerReturns = createAsyncThunk(
//   "supplierPayments/fetchDueCustomerReturns",
//   async (customerId, { rejectWithValue }) => {
//     try {
//       const res = await postData(
//         `${path}/get-due-customer-returns`,
//         { customerId }
//       );
//       return { customerId, returns: res.data };
//     } catch (err) {
//       return rejectWithValue(err.response?.data || err.message);
//     }
//   }
// );
//
// And add to supplierPaymentSlice initialState:
//   dueCustomerReturns: {},
//   customerRefundBalances: {},
//
// And add reducer cases:
//   builder.addCase(fetchDueCustomerReturns.fulfilled, (state, action) => {
//     state.dueCustomerReturns[action.payload.customerId] = action.payload.returns;
//   });



const SupplierPayments = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const { modal } = App.useApp();
  const [form] = Form.useForm();
  const authInfo = useSelector(selectAuthInfo);

  // ✅ payment mode toggle
  const [paymentMode, setPaymentMode] = useState("supplier_payment");
  const isRefundMode = paymentMode === "customer_refund";

  const [rows, setRows] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [tagGroupList, setTagGroupList] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagModal, setTagModal] = useState(false);
  const [totalPaid, setTotalPaid] = useState(0);

  const {
    entryDate,
    paymentCode,
    dueVouchers,
    dueCustomerReturns,
    balances,
    accountBalances,
    selectedPayment,
    isEditPayment,
    directIncome,
    currentLiability,
  } = useSelector((state) => state.supplierPayments);

  const { suppliers, loadIntoAccs, loadIntoAccounts, customers } = useSelector((s) => s.lookups);
  let [access_type, access_type_set] = useState("");

  useEffect(() => {
    const access = getAccessType("creditor_payment");
    access_type_set(access);
  }, []);

  useEffect(() => {
    if (!id) dispatch(fetchPaymentCode());
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(fetchSuppliers());
    dispatch(fetchCustomers());
    dispatch(fetchLoadIntoAccs());
    dispatch(fetchLoadIntoAccsExcludeCheque());
    dispatch(fetchCurrentliablityAccounts());
    dispatch(fetchDirectIncomeAccounts());
    dispatch(fetchTagGroups())
      .unwrap()
      .then((groups) =>
        Promise.all(groups.map((g) => dispatch(fetchTagGroupFields(g.id)).unwrap()))
      )
      .then((allFields) => {
        const combined = allFields.flat().map((item) => ({
          ...item.raw,
          combinedField: item.display_text,
          id: item.id,
        }));
        setTagGroupList(combined);
      })
      .catch(() => {});

    setRows([createEmptyRow(null)]);
  }, [dispatch]);

  useEffect(() => {
    if (id && !selectedPayment) {
      dispatch(fetchPayments())
        .unwrap()
        .then((list) => {
          const found = list.find((r) => r.pay_id == id);
          if (found) {
            dispatch(fetchSupplierBalance(found.acc_id));
            dispatch(setSelectedPayment(found));
            dispatch(setIsEditPayment(true));
            dispatch(setPaymentCode(found.pay_code));
            if (found.payment_type === "customer_refund") {
              setPaymentMode("customer_refund");
            }
          }
        })
        .catch(() => {});
    }
  }, [id, selectedPayment, dispatch]);

  useEffect(() => {
    if (id && selectedPayment) {
      dispatch(fetchSupplierBalance(selectedPayment.acc_id));
      dispatch(setIsEditPayment(true));
      dispatch(setPaymentCode(selectedPayment.pay_code));
      if (selectedPayment.payment_type === "customer_refund") {
        setPaymentMode("customer_refund");
      }
    }
  }, [id, selectedPayment, dispatch]);

  useEffect(() => {
    if (id && selectedPayment && loadIntoAccs.length > 0) {
      selectedPayment.details.forEach((d) => {
        const acc = loadIntoAccs.find((a) => a.acc_id === d.from_acc_id);
        if (acc?.acc_id && acc?.acc_type_id) {
          dispatch(fetchAccountBalance({ acc_id: acc.acc_id, accType: `'${acc.acc_type_id}'` }));
        }
      });
    }
  }, [isEditPayment, selectedPayment, loadIntoAccs, dispatch]);

  useEffect(() => {
    if (isEditPayment && selectedPayment) {
      form.setFieldsValue({
        pay_date: dayjs(selectedPayment.created_date),
        description: selectedPayment.narration,
        tags: selectedPayment.tag_field_id || [],
      });
      if (selectedPayment.tag_field_id) setSelectedTags(selectedPayment.tag_field_id);

      setRows(
        (selectedPayment.details || []).map((d) => ({
          key: Date.now() + Math.random(),
          from_acc_id: d.from_acc_id,
          from_acc_name: d.from_acc_name || "",
          from_acc_type_id: d.from_acc_type_id,
          from_supplier: {
            acc_id: d.to_acc_id || d.from_acc_id,
            acc_name: d.to_acc_name || d.from_acc_name,
          },
          voucher_no: d.voucher_no,
          emi_id: d.emi_id,
          emi_no: d.emi_no,
          amount: d.pay_amount || 0,
          discount: d.discount_amount || 0,
          original_amount: d.pay_amount || 0,
          cheque_number: d.cheque_number || "",
          pdc_date: d.pdc_date ? dayjs(d.pdc_date) : "",
          expected_bank_acc_id: d.expected_bank_acc_id || null,
          voucher_details: null,
        }))
      );

      dispatch(fetchDueVouchers(selectedPayment.acc_id));

      const first = selectedPayment.details?.[0];
      if (first) {
        if (selectedPayment.payment_type === "customer_refund") {
          setSelectedCustomer({
            acc_id: first.to_acc_id,
            acc_name: first.to_acc_name,
          });
          dispatch(fetchDueCustomerReturns(first.to_acc_id));
        } else {
          setSelectedSupplier({
            acc_id: first.to_acc_id || first.from_acc_id,
            acc_name: first.to_acc_name || first.from_acc_name,
          });
        }
      }
    }
  }, [isEditPayment, selectedPayment, form]);

  useEffect(() => {
    if (!isEditPayment || !selectedPayment) return;
    const supplierVouchers = dueVouchers[selectedPayment.acc_id];
    if (!supplierVouchers || supplierVouchers.length === 0) return;

    setRows((prevRows) =>
      prevRows.map((row) => {
        const matched = supplierVouchers.find((v) => v.voucher_no === row.voucher_no);
        return { ...row, voucher_details: matched || { no_due: true } };
      })
    );
  }, [dueVouchers, selectedPayment, isEditPayment]);

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    if (supplier?.acc_id) {
      dispatch(fetchDueVouchers(supplier.acc_id));
      dispatch(fetchSupplierBalance(supplier.acc_id));
    }
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    if (customer?.acc_id) {
      dispatch(fetchDueCustomerReturns(customer.acc_id));
    }
  };

  const handleAddRow = () => {
    setRows([...rows, createEmptyRow(isRefundMode ? selectedCustomer : selectedSupplier)]);
  };

  const handleRemoveRow = (key) => setRows(rows.filter((r) => r.key !== key));

  const handleCancel = () => {
    dispatch(setSelectedPayment(null));
    dispatch(setIsEditPayment(false));
    navigate("/accounts/creditor-payments");
  };

  const createEmptyRow = (entity) => ({
    key: Date.now() + Math.random(),
    from_supplier: entity,
    from_acc_name: "",
    from_acc_id: 0,
    from_acc_type_id: "",
    voucher_no: "",
    voucher_details: null,
    emi_no: "",
    amount: 0,
    discount: 0,
    expected_bank_acc_id: null,
  });

  // FIFO distribute
  const distributePayment = (amount) => {
    setTotalPaid(amount);
    let remaining = Number(amount);
    const newRows = rows.map((row) => {
      const due =
        Number(row.voucher_details?.due || 0) +
        (isEditPayment ? Number(row.original_amount || 0) : 0);
      const allocated = Math.min(remaining, due);
      remaining = Math.max(0, remaining - allocated);
      return { ...row, amount: allocated };
    });
    setRows(newRows);
  };

  const redistributeAfterSelect = (updatedRows) => {
    if (totalPaid <= 0) { setRows(updatedRows); return; }
    let remaining = Number(totalPaid);
    const redistributed = updatedRows.map((row) => {
      const due =
        Number(row.voucher_details?.due || 0) +
        (isEditPayment ? Number(row.original_amount || 0) : 0);
      const allocated = Math.min(remaining, due);
      remaining = Math.max(0, remaining - allocated);
      return { ...row, amount: allocated };
    });
    setRows(redistributed);
  };

  let finalEntryDate = entryDate;
  if (!dayjs(entryDate).isSame(dayjs(), "day")) {
    finalEntryDate = getDateTimeFromSelectedDate(entryDate);
  }

  // ─── Save ──────────────────────────────────────────────────────────────────
  const onFinish = async (values) => {
    try {
      const totalAmount = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
      const totalDiscount = rows.reduce((sum, r) => sum + Number(r.discount || 0), 0);

      if (isRefundMode) {
        // ── Customer Refund validation ──
        if (!selectedCustomer?.acc_id) {
          modal.error({ title: "Please select a customer before saving." });
          return;
        }
        for (let r of rows) {
          const isOBRow = r.voucher_no === "__OB__";
          if (!r.voucher_no && !isOBRow) {
            modal.error({ title: "Please select a Sales Return voucher before saving." });
            return;
          }
          if (r.amount == null || r.amount <= 0) {
            modal.error({ title: "Please enter a valid Amount before saving." });
            return;
          }
          if (!isOBRow) {
            const due = Number(r.voucher_details?.due || 0) +
              (isEditPayment ? Number(r.original_amount || 0) : 0);
            if (Number(r.amount) > due + 0.01) {
              modal.error({
                title: "Amount Exceeded",
                content: `Refund for ${r.voucher_no} cannot exceed pending refund of ${due.toFixed(2)}`,
              });
              return;
            }
          }
          if (!r.from_acc_name) {
            modal.error({ title: "Please select Account (cash/bank) to refund from." });
            return;
          }
        }

        const payPayload = {
          pay_id: isEditPayment ? selectedPayment.pay_id : 0,
          action: isEditPayment ? "update" : "create",
          pay_code: paymentCode,
          created_date: formatToUtc(finalEntryDate),
          narration: values.description || "",
          pay_total: totalAmount,
          discount_total: totalDiscount,
          tax_total: 0,
          payment_type: "customer_refund",  // ✅ key flag
        };

        if (selectedTags.length > 0) payPayload.tag_field_id = selectedTags;

        const payload = {
          pay: payPayload,
          payDetail: rows.map((r) => ({
            to_acc_id: selectedCustomer.acc_id,       // money goes TO customer
            to_acc_name: selectedCustomer.acc_name,
            from_acc_id: r.from_acc_id || 0,          // money comes FROM cash/bank
            from_acc_name: r.from_acc_name || "",
            cheque_number: r.cheque_number || "",
            voucher_no: r.voucher_no === "__OB__" ? null : (r.voucher_no || ""),
            pay_amount: Number(r.amount) || 0,
            discount_amount: 0,
            tax_amount: 0,
            pay_total: Number(r.amount) || 0,
            pdc_date: r.pdc_date ? formatToUtcString(r.pdc_date) : formatToUtcString(dayjs()),
            acc_type_id: r.from_acc_type_id || "",
            expected_bank_acc_id: r.expected_bank_acc_id || null,
            direct_income_id: directIncome?.acc_id,
            current_liability_id: currentLiability?.acc_id,
          })),
        };

        const res = await dispatch(savePayment(payload)).unwrap();
        modal.success({ title: res.msg || "Customer refund saved successfully" });

      } else {
        // ── Supplier Payment validation ──
        const supplier = rows[0]?.from_supplier;
        if (!supplier?.acc_id) {
          modal.error({ title: "Please select a supplier before saving." });
          return;
        }
        const allSameSupplier = rows.every((r) => r.from_supplier?.acc_id === supplier?.acc_id);
        if (!allSameSupplier) {
          modal.error({ title: "All rows must belong to the same supplier" });
          return;
        }

        for (let r of rows) {
          const isOBRow = r.voucher_no === "__OB__";
          if (!r.voucher_no && !isOBRow) {
            modal.error({ title: "Please select Voucher before saving." });
            return;
          }
          if (r.amount == null || r.amount <= 0) {
            modal.error({ title: "Please enter a valid Amount before saving." });
            return;
          }
          if (!isOBRow) {
            const voucherDue = Number(r.voucher_details?.due || 0);
            const originalAmount = isEditPayment ? Number(r.original_amount || 0) : 0;
            const availableDue = voucherDue + originalAmount;
            if (Number(r.amount) > availableDue + 0.01) {
              modal.error({
                title: "Amount Exceeded",
                content: `Amount for voucher ${r.voucher_no} cannot exceed due amount of ${availableDue.toFixed(2)}`,
              });
              return;
            }
          }
          if (!r.from_acc_name) {
            modal.error({ title: "Please select Account before saving." });
            return;
          }
        }

        const payPayload = {
          pay_id: isEditPayment ? selectedPayment.pay_id : 0,
          action: isEditPayment ? "update" : "create",
          pay_code: paymentCode,
          created_date: formatToUtc(finalEntryDate),
          narration: values.description || "",
          pay_total: totalAmount,
          discount_total: totalDiscount,
          tax_total: 0,
          payment_type: "supplier_payment",
        };

        if (selectedTags.length > 0) payPayload.tag_field_id = selectedTags;

        const payload = {
          pay: payPayload,
          payDetail: rows.map((r) => ({
            to_acc_id: r.from_supplier?.acc_id || 0,
            to_acc_name: r.from_supplier?.acc_name || "",
            from_acc_id: r.from_acc_id || 0,
            from_acc_name: r.from_acc_name || "",
            cheque_number: r.cheque_number || "",
            voucher_no: r.voucher_no === "__OB__" ? null : (r.voucher_no || ""),
            pay_amount: Number(r.amount) || 0,
            discount_amount: Number(r.discount) || 0,
            tax_amount: 0,
            pay_total: (Number(r.amount) || 0) - (Number(r.discount) || 0),
            pdc_date: r.pdc_date ? formatToUtcString(r.pdc_date) : formatToUtcString(dayjs()),
            acc_type_id: r.from_acc_type_id || "",
            expected_bank_acc_id: r.expected_bank_acc_id || null,
            direct_income_id: directIncome?.acc_id,
            current_liability_id: currentLiability?.acc_id,
          })),
        };

        const res = await dispatch(savePayment(payload)).unwrap();
        modal.success({ title: res.msg || "Payment saved successfully" });
      }

      dispatch(setSelectedPayment(null));
      dispatch(setIsEditPayment(false));
      dispatch(setRefreshList(true));
      form.resetFields();
      setRows([]);
      setTotalPaid(0);
      setSelectedCustomer(null);
      setSelectedSupplier(null);
      navigate("/accounts/creditor-payments");
    } catch (err) {
      modal.error({ title: err.message || "Failed to save" });
    }
  };

  // ─── Dynamic columns ───────────────────────────────────────────────────────
  const pdcColumn = rows.some((r) => r.from_acc_name?.toLowerCase() === "pdc")
    ? [{
        title: "PDC Date",
        dataIndex: "pdc_date",
        width: 160,
        render: (_, record, index) =>
          record.from_acc_name?.toLowerCase() === "pdc" ? (
            <DatePicker
              style={{ width: "100%" }}
              value={record.pdc_date ? dayjs(record.pdc_date) : null}
              onChange={(d) => {
                const newRows = [...rows];
                newRows[index].pdc_date = d ? d.toISOString() : null;
                setRows(newRows);
              }}
              format={dateTimeFormat}
            />
          ) : "-",
      }]
    : [];

  const chequeNumberColumn = rows.some((r) => r.from_acc_type_id?.toLowerCase() === "cheque")
    ? [{
        title: "Cheque Number",
        dataIndex: "cheque_number",
        width: 160,
        render: (_, record, index) =>
          record.from_acc_type_id?.toLowerCase() === "cheque" ? (
            <Input
              placeholder="Enter Cheque Number"
              value={record.cheque_number}
              onChange={(e) => {
                const newRows = [...rows];
                newRows[index].cheque_number = e.target.value;
                setRows(newRows);
              }}
            />
          ) : "-",
      }]
    : [];

  const expectedBankColumn = rows.some((r) => r.from_acc_type_id?.toLowerCase() === "cheque")
    ? [{
        title: "Expected Bank",
        dataIndex: "expected_bank_acc_id",
        width: 180,
        render: (_, record, index) =>
          record.from_acc_type_id?.toLowerCase() === "cheque" ? (
            <Select
              allowClear
              placeholder="Expected Bank (optional)"
              style={{ width: "100%" }}
              value={record.expected_bank_acc_id || undefined}
              options={(loadIntoAccounts || []).map((acc) => ({
                label: acc.acc_name,
                value: acc.acc_id,
              }))}
              onChange={(v) => {
                const newRows = [...rows];
                newRows[index].expected_bank_acc_id = v || null;
                setRows(newRows);
              }}
            />
          ) : "-",
      }]
    : [];

  // ─── Supplier payment columns ──────────────────────────────────────────────
  const supplierColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 50 },
    {
      title: "Supplier",
      dataIndex: "from_supplier",
      render: (supplier, record, index) => {
        const prevDue = supplier?.acc_id ? balances[supplier.acc_id]?.total_balance || 0 : 0;
        return (
          <div>
            <SearchListAutoComplete
              entityType="supplier"
              style={{ width: "100%" }}
              optionsList={(suppliers || []).map((c) => ({
                sale_id: c.acc_id,
                display_text: [c.acc_name, c.contact_no, c.institution_name].filter(Boolean).join(" - "),
                raw: c,
              }))}
              onSelect={(obj) => {
                const newSupplier = obj?.raw;
                const currentSupplier = rows.find((r) => r.from_supplier?.acc_id)?.from_supplier;
                if (currentSupplier && currentSupplier?.acc_id !== newSupplier?.acc_id) {
                  setSelectedSupplier(newSupplier);
                  setRows([createEmptyRow(newSupplier)]);
                  dispatch(fetchSupplierBalance(newSupplier?.acc_id));
                  dispatch(fetchDueVouchers(newSupplier?.acc_id));
                  return;
                }
                const newRows = [...rows];
                newRows[index].from_supplier = newSupplier;
                setRows(newRows);
                setSelectedSupplier(newSupplier);
                handleSupplierSelect(newSupplier);
              }}
              value={selectedSupplier ? { value: selectedSupplier.acc_name, label: selectedSupplier.acc_name } : null}
              allowClear
              onClear={() => { setSelectedSupplier(null); setRows([createEmptyRow(null)]); }}
              placeholder="Supplier"
              showAddNew={false}
            />
            {selectedSupplier && (
              <div style={{ color: "blue", fontSize: 12, marginTop: 4 }}>
                Previous Due: {Number(prevDue).toFixed(2)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "From Account",
      dataIndex: "from_acc_name",
      render: (_, record, index) => {
        const accountBalance = record.from_acc_id ? accountBalances[record.from_acc_id] : null;
        return (
          <div>
            <SearchListAutoComplete
              entityType="accounts"
              style={{ width: "100%" }}
              optionsList={(loadIntoAccs || []).map((acc) => ({
                sale_id: acc.acc_id,
                display_text: acc.display_text || acc.acc_name,
                raw: acc,
              }))}
              onSelect={(obj) => {
                const newRows = [...rows];
                newRows[index].from_acc_name = obj?.raw?.acc_name || "";
                newRows[index].from_acc_id = obj?.raw?.acc_id || 0;
                newRows[index].from_acc_type_id = obj?.raw?.acc_type_id || "";
                setRows(newRows);
                if (obj?.raw?.acc_id) {
                  dispatch(fetchAccountBalance({ acc_id: obj?.raw?.acc_id, accType: `'${obj?.raw?.acc_type_id}'` }));
                }
              }}
              value={record.from_acc_name ? { value: record.from_acc_name, label: record.from_acc_name } : null}
              placeholder="Cash / Bank / Cheque"
              showAddNew={false}
            />
            {accountBalance !== null && (
              <div style={{ color: "blue", fontSize: 12, marginTop: 4 }}>
                Balance: {Number(accountBalance).toFixed(2)}
              </div>
            )}
          </div>
        );
      },
    },
    ...pdcColumn,
    ...chequeNumberColumn,
    ...expectedBankColumn,
    {
      title: "Due Voucher",
      dataIndex: "voucher_no",
      render: (_, record, index) => {
        const supplierVouchers = selectedSupplier?.acc_id ? dueVouchers[selectedSupplier.acc_id] || [] : [];
        const openingBalance = balances[selectedSupplier?.acc_id]?.opening_balance || 0;
        const obOption = openingBalance > 0
          ? [{ sale_id: "__OB__", display_text: `Opening Balance — Due: ${Number(openingBalance).toFixed(2)}`, raw: { voucher_no: "__OB__", due: openingBalance, total_amount: openingBalance, paid: 0, discountAmount: 0, is_opening_balance: true } }]
          : [];
        const allOptions = [...obOption, ...supplierVouchers.map((v) => ({ sale_id: v.voucher_no, display_text: v.display_text || v.voucher_no, raw: v }))];
        const isOBRow = record.voucher_no === "__OB__";
        return (
          <div>
            <SearchListAutoComplete
              entityType="vouchers"
              style={{ width: "100%" }}
              optionsList={allOptions}
              onSelect={(obj) => {
                const newRows = [...rows];
                newRows[index].voucher_no = obj?.raw?.voucher_no || "";
                newRows[index].voucher_details = obj?.raw || null;
                redistributeAfterSelect(newRows);
              }}
              value={record.voucher_no ? { value: isOBRow ? "Opening Balance" : record.voucher_no, label: isOBRow ? "Opening Balance" : record.voucher_no } : null}
              placeholder="Voucher"
              showAddNew={false}
            />
            {record.voucher_details && (
              isOBRow || record.voucher_details.is_opening_balance ? (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  <span style={{ color: "red", marginRight: 8 }}>Due: {Number(record.voucher_details.due || 0).toFixed(2)}</span>
                  <span style={{ color: "gray" }}>Opening Balance</span>
                </div>
              ) : record.voucher_details.no_due ? (
                <div style={{ fontSize: 12, marginTop: 4, color: "gray" }}>No Due</div>
              ) : (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  <span style={{ color: "red", marginRight: 8 }}>Due: {(Number(record.voucher_details.due || 0) + (isEditPayment ? Number(record.original_amount || 0) : 0)).toFixed(2)}</span>
                  <span style={{ color: "brown", marginRight: 8 }}>Discount: {Number(record.voucher_details.discountAmount || 0).toFixed(2)}</span>
                  <span style={{ color: "green", marginRight: 8 }}>Paid: {Number(record.voucher_details.paid || 0).toFixed(2)}</span>
                  <span>Bill: {Number(record.voucher_details.total_amount || 0).toFixed(2)}</span>
                </div>
              )
            )}
          </div>
        );
      },
    },
    {
      title: "Payment",
      dataIndex: "amount",
      render: (_, record, index) => (
        <InputNumberField min={0} value={record.amount} stringMode step={1} precision={2} style={{ width: "100%" }}
          onFocus={() => {
            const newRows = [...rows];
            newRows[index].amount = undefined;
            setRows(newRows);}}
          onChange={(val) => { const newRows = [...rows]; newRows[index].amount = val || 0; setRows(newRows); }} />
      ),
    },
    {
      title: "Discount",
      dataIndex: "discount",
      width: 140,
      render: (_, record, index) => (
        <InputNumberField min={0} value={record.discount} stringMode step={1} precision={2} style={{ width: "100%" }}
          onFocus={() => {
            const newRows = [...rows];
            newRows[index].discount = undefined;
            setRows(newRows);
          }}
          onChange={(val) => { const newRows = [...rows]; newRows[index].discount = val || 0; setRows(newRows); }} />
      ),
    },
    { title: "Actions", render: (_, record) => <Button type="link" danger onClick={() => handleRemoveRow(record.key)} icon={<DeleteOutlined />}/>},
  ];

  // ─── Customer Refund columns ───────────────────────────────────────────────
  const refundColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 50 },
    {
      title: "Customer",
      dataIndex: "from_supplier",
      render: (_, record, index) => (
        <SearchListAutoComplete
          entityType="customer"
          style={{ width: "100%" }}
          optionsList={(customers || []).map((c) => ({
            sale_id: c.acc_id,
            display_text: c.acc_name,
            raw: c,
          }))}
          value={selectedCustomer ? { value: selectedCustomer.acc_name, label: selectedCustomer.acc_name } : null}
          allowClear
          onClear={() => { setSelectedCustomer(null); setRows([createEmptyRow(null)]); }}
          onSelect={(obj) => {
            const newCustomer = obj?.raw;
            if (!newCustomer?.acc_id) return;
            const currentCustomer = selectedCustomer;
            if (currentCustomer && currentCustomer.acc_id !== newCustomer.acc_id) {
              setSelectedCustomer(newCustomer);
              setRows([createEmptyRow(newCustomer)]);
              dispatch(fetchDueCustomerReturns(newCustomer.acc_id));
              return;
            }
            setSelectedCustomer(newCustomer);
            setRows([createEmptyRow(newCustomer)]);
            handleCustomerSelect(newCustomer);
          }}
          placeholder="Customer"
          showAddNew={false}
        />
      ),
    },
    {
      title: "Refund From Account",
      dataIndex: "from_acc_name",
      render: (_, record, index) => {
        const accountBalance = record.from_acc_id ? accountBalances[record.from_acc_id] : null;
        return (
          <div>
            <SearchListAutoComplete
              entityType="accounts"
              style={{ width: "100%" }}
              optionsList={(loadIntoAccs || []).map((acc) => ({
                sale_id: acc.acc_id,
                display_text: acc.display_text || acc.acc_name,
                raw: acc,
              }))}
              onSelect={(obj) => {
                const newRows = [...rows];
                newRows[index].from_acc_name = obj?.raw?.acc_name || "";
                newRows[index].from_acc_id = obj?.raw?.acc_id || 0;
                newRows[index].from_acc_type_id = obj?.raw?.acc_type_id || "";
                setRows(newRows);
                if (obj?.raw?.acc_id) {
                  dispatch(fetchAccountBalance({ acc_id: obj?.raw?.acc_id, accType: `'${obj?.raw?.acc_type_id}'` }));
                }
              }}
              value={record.from_acc_name ? { value: record.from_acc_name, label: record.from_acc_name } : null}
              placeholder="Cash / Bank"
              showAddNew={false}
            />
            {accountBalance !== null && (
              <div style={{ color: "blue", fontSize: 12, marginTop: 4 }}>
                Balance: {Number(accountBalance).toFixed(2)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Sales Return Voucher",
      dataIndex: "voucher_no",
      render: (_, record, index) => {
        const customerReturns = selectedCustomer?.acc_id
          ? dueCustomerReturns?.[selectedCustomer.acc_id] || []
          : [];
        const allOptions = customerReturns.map((r) => ({
          sale_id: r.voucher_no,
          display_text: r.display_text,
          raw: { ...r, due: r.due },
        }));
        return (
          <div>
            <SearchListAutoComplete
              entityType="vouchers"
              style={{ width: "100%" }}
              optionsList={allOptions}
              onSelect={(obj) => {
                const newRows = [...rows];
                newRows[index].voucher_no = obj?.raw?.voucher_no || "";
                newRows[index].voucher_details = obj?.raw || null;
                redistributeAfterSelect(newRows);
              }}
              value={record.voucher_no ? { value: record.voucher_no, label: record.voucher_no } : null}
              placeholder="Sales Return Voucher"
              showAddNew={false}
            />
            {record.voucher_details && !record.voucher_details.no_due && (
              <div style={{ fontSize: 12, marginTop: 4 }}>
                <span style={{ color: "orange", marginRight: 8 }}>
                  Pending Refund: {Number(record.voucher_details.due || 0).toFixed(2)}
                </span>
                <span style={{ color: "green", marginRight: 8 }}>
                  Already Refunded: {Number(record.voucher_details.paid_amount || 0).toFixed(2)}
                </span>
                <span>Total Return: {Number(record.voucher_details.total_amount || 0).toFixed(2)}</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Refund Amount",
      dataIndex: "amount",
      render: (_, record, index) => (
        <InputNumberField min={0} value={record.amount} stringMode step={1} precision={2} style={{ width: "100%" }}
          onFocus={() => {
            const newRows = [...rows];
            newRows[index].amount = undefined;
            setRows(newRows);
          }}
          onChange={(val) => { const newRows = [...rows]; newRows[index].amount = val || 0; setRows(newRows); }} />
      ),
    },
    { title: "Actions", render: (_, record) => <Button type="link" danger onClick={() => handleRemoveRow(record.key)}icon={<DeleteOutlined />} /> },
  ];

  const totalAmount = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
  const totalDiscount = rows.reduce((sum, r) => sum + Number(r.discount || 0), 0);
  const { disabledDate } = useMigrationDate();

  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>
        {isRefundMode ? "Customer Refund" : "Supplier Payments"}
      </Title>

      {/* ✅ Payment mode toggle */}
      {!isEditPayment && (
        <div style={{ marginBottom: 20 }}>
          <Radio.Group
            value={paymentMode}
            onChange={(e) => {
              setPaymentMode(e.target.value);
              setRows([createEmptyRow(null)]);
              setSelectedSupplier(null);
              setSelectedCustomer(null);
              setTotalPaid(0);
            }}
            buttonStyle="solid"
          >
            <Radio.Button value="supplier_payment">Supplier Payment</Radio.Button>
            <Radio.Button value="customer_refund">Customer Refund</Radio.Button>
          </Radio.Group>
        </div>
      )}

      {/* ✅ Customer refund info banner */}
      {isRefundMode && (
        <div style={{ background: "#fff7e6", border: "1px solid #ffd591", borderRadius: 6, padding: "8px 16px", marginBottom: 16, fontSize: 13, color: "#d46b08" }}>
          Customer Refund mode — select a customer to see pending sales return refunds. Money flows OUT from your cash/bank TO the customer.
        </div>
      )}

      <Form
        form={form}
        layout="horizontal"
        style={{ width: "100%", maxWidth: "1400px" }}
        colon={false}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
        initialValues={{ pay_date: entryDate ? dayjs(entryDate) : null }}
        onFinish={onFinish}
      >
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{ flexDirection: !screens.md || !screens.xl ? "column" : "row", width: "100%", marginBottom: "24px" }}
        >
          <Flex vertical gap={10} style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}>
            <Form.Item label="Payment Code">
              <Input value={paymentCode} disabled />
            </Form.Item>
            <Form.Item name="pay_date" label="Payment Date" rules={[{ required: true, message: "Please select date" }]}>
              <DatePicker
                style={{ width: "100%" }}
                value={entryDate ? dayjs(entryDate) : null}
                onChange={(d) => dispatch(setEntryDate(d ? d.toISOString() : null))}
                format={dateTimeFormat}
                disabledDate={disabledDate}
              />
            </Form.Item>
            <Form.Item name="tags" label="Tags">
              <Select
                mode="multiple"
                maxTagCount="responsive"
                className="tag-multiselect"
                allowClear
                style={{ width: "100%" }}
                placeholder="Choose Tag Groups"
                options={tagGroupList.map((item) => ({ value: item.id, label: item.combinedField }))}
                onChange={(ids) => setSelectedTags(ids)}
                popupRender={(menu) => (
                  <>
                    {menu}
                    <div style={{ display: "flex", justifyContent: "center", padding: 8, borderTop: "1px solid #f0f0f0" }}>
                      <Button type="link" onClick={() => setTagModal(true)}>+ Add New Tag</Button>
                    </div>
                  </>
                )}
              />
            </Form.Item>
          </Flex>
        </Flex>

        {/* ✅ Total paid FIFO input */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <span style={{ fontWeight: 500 }}>
            {isRefundMode ? "Total Refund Amount:" : "Total Paid:"}
          </span>
          <InputNumberField
            min={0} precision={2} step={1} stringMode
            style={{ width: 180 }}
            value={totalPaid}
            onFocus={() => setTotalPaid(undefined)}
            onChange={(val) => distributePayment(val || 0)}
            placeholder={isRefundMode ? "Enter refund amount" : "Enter total payment"}
          />
          <span style={{ fontSize: 13, color: "#888" }}>
            Auto-distributes FIFO across rows below
          </span>
        </div>

        <Button type="dashed" onClick={handleAddRow} style={{ marginBottom: 12 }}>
          + Add Row
        </Button>

        <Table
          dataSource={rows}
          columns={isRefundMode ? refundColumns : supplierColumns}
          pagination={false}
          rowKey="key"
          bordered
          scroll={{ x: "max-content" }}
          style={{ marginTop: 16, marginBottom: 24 }}
          footer={() => (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <strong>
                Total Amount: {Number(totalAmount).toFixed(2)}
                {!isRefundMode && ` | Total Discount: ${Number(totalDiscount).toFixed(2)}`}
              </strong>
            </div>
          )}
        />

        <Form.Item name="description" label="Description">
          <TextArea rows={3} />
        </Form.Item>

        <Space style={{ marginTop: 16 }}>
          <Button onClick={handleCancel}>Back</Button>
          <Button
            onClick={() => {
              form.resetFields();
              dispatch(resetPayments());
              setSelectedSupplier(null);
              setSelectedCustomer(null);
              setSelectedTags([]);
              setRows([]);
              setTotalPaid(0);
              dispatch(fetchPaymentCode());
            }}
          >
            Reset
          </Button>
          {access_type == "WRITE" && (
            <Button type="primary" htmlType="submit">
              {isRefundMode ? "Save Refund" : "Save Payment"}
            </Button>
          )}
        </Space>
      </Form>

      <AddNewModal open={tagModal} title="Add Tag" onClose={() => setTagModal(false)}>
        <TagCreateForm
          authInfo={authInfo}
          onSuccess={async ({ createdName }) => {
            const res = await dispatch(fetchTagGroupFields("")).unwrap();
            const match = res.find((t) => t.raw.field_name.toLowerCase() === createdName.toLowerCase());
            if (!match) { message.warning("Tag creation failed or tag not found"); return; }
            setTagGroupList((prev) => {
              const exists = prev.some((p) => p.id === match.id);
              return exists ? prev : [...prev, { ...match.raw, combinedField: match.display_text, id: match.id }];
            });
            const current = form.getFieldValue("tags") || [];
            if (current.includes(match.id)) { message.info("Tag already exists and is already selected"); return; }
            form.setFieldsValue({ tags: [...current, match.id] });
            setSelectedTags([...current, match.id]);
            message.success("Tag added");
            setTagModal(false);
          }}
        />
      </AddNewModal>
    </div>
  );
};

export default SupplierPayments;